import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, Btn, Field, Input } from "@/components/admin/ui";
import { API } from "@/config";
import { Calendar, CheckCircle, ExternalLink, Link2Off, RefreshCw } from "lucide-react";

export const CalendarSettings = () => {
  const [params] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [creds, setCreds] = useState({ client_id: "", client_secret: "" });
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/admin/calendar-settings/status`);
    setStatus(r.data);
  }, []);

  useEffect(() => {
    load();
    if (params.get("connected") === "1") toast.success("Google Calendar connected!");
    if (params.get("error")) toast.error(`Connection failed: ${params.get("error")}`);
  }, [load, params]);

  const saveCreds = async () => {
    if (!creds.client_id.trim() || !creds.client_secret.trim()) {
      return toast.error("Enter both Client ID and Client Secret");
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/calendar-settings/credentials`, creds);
      toast.success("Credentials saved — click Connect to authorize");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save credentials");
    } finally {
      setSaving(false);
    }
  };

  const connect = async () => {
    setConnecting(true);
    try {
      const r = await axios.get(`${API}/admin/calendar-settings/connect`);
      // Open OAuth in same tab so cookies work
      window.location.href = r.data.authorization_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not get authorization URL");
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Calendar? Future bookings won't get calendar events until you reconnect.")) return;
    await axios.delete(`${API}/admin/calendar-settings/disconnect`);
    toast.success("Disconnected");
    load();
  };

  return (
    <div className="max-w-2xl space-y-8">
      <div>
        <h1 className="font-serif text-4xl mb-1">Google Calendar</h1>
        <p className="text-sm text-[#B9C8D5]">
          When a reservation is confirmed, two events are automatically added to your Google Calendar —
          one for Delivery and one for Pickup — with full order details in the description.
        </p>
      </div>

      {/* Connection status */}
      <Card title="Connection Status">
        {status?.connected ? (
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
              <div>
                <div className="text-[#F6F1E4] font-medium">Connected</div>
                <div className="text-xs text-[#B9C8D5]">{status.connected_email}</div>
              </div>
            </div>
            <Btn variant="ghost" onClick={disconnect} data-testid="gcal-disconnect-btn">
              <Link2Off className="w-4 h-4 mr-1.5" /> Disconnect
            </Btn>
          </div>
        ) : (
          <div className="flex items-center gap-3 text-[#B9C8D5]">
            <Calendar className="w-5 h-5 opacity-50" />
            <span className="text-sm">Not connected</span>
          </div>
        )}
      </Card>

      {/* Credentials setup */}
      {!status?.connected && (
        <Card title="Step 1 — Google Cloud Credentials">
          <p className="text-xs text-[#B9C8D5] mb-4 leading-relaxed">
            You need a Google Cloud project with the Calendar API enabled. Follow the steps below, then paste
            your credentials here.
          </p>

          <ol className="text-xs text-[#B9C8D5] space-y-2 mb-6 list-decimal list-inside leading-relaxed">
            <li>Go to <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="text-[#FFD166] underline inline-flex items-center gap-0.5">console.cloud.google.com <ExternalLink className="w-2.5 h-2.5"/></a> and create a project</li>
            <li><strong className="text-[#F6F1E4]">APIs &amp; Services → Library</strong> → enable <strong className="text-[#F6F1E4]">Google Calendar API</strong></li>
            <li><strong className="text-[#F6F1E4]">APIs &amp; Services → OAuth consent screen</strong> → External → fill in app name &amp; your email, add scope <code className="bg-black/30 px-1 rounded">https://www.googleapis.com/auth/calendar</code>, add yourself as a test user</li>
            <li><strong className="text-[#F6F1E4]">APIs &amp; Services → Credentials → Create OAuth Client ID</strong> → Web application</li>
            <li>Add these Authorized redirect URIs:<br />
              <code className="block bg-black/30 px-2 py-1 rounded mt-1 break-all">https://enchantedgearrentals.com/api/admin/calendar-settings/oauth/callback</code>
              <code className="block bg-black/30 px-2 py-1 rounded mt-1 break-all">https://driver-scan-hub-1.preview.emergentagent.com/api/admin/calendar-settings/oauth/callback</code>
            </li>
            <li>Copy the <strong className="text-[#F6F1E4]">Client ID</strong> and <strong className="text-[#F6F1E4]">Client Secret</strong> and paste below</li>
          </ol>

          <div className="space-y-4">
            <Field label="Google Client ID">
              <Input
                placeholder="xxxx.apps.googleusercontent.com"
                value={creds.client_id}
                onChange={(e) => setCreds({ ...creds, client_id: e.target.value })}
                data-testid="gcal-client-id-input"
              />
            </Field>
            <Field label="Google Client Secret">
              <Input
                type="password"
                placeholder="GOCSPX-..."
                value={creds.client_secret}
                onChange={(e) => setCreds({ ...creds, client_secret: e.target.value })}
                data-testid="gcal-client-secret-input"
              />
            </Field>
            <Btn onClick={saveCreds} disabled={saving} data-testid="gcal-save-creds-btn">
              {saving ? "Saving…" : "Save Credentials"}
            </Btn>
          </div>
        </Card>
      )}

      {/* Connect button */}
      {status?.has_credentials && !status?.connected && (
        <Card title="Step 2 — Authorize Calendar Access">
          <p className="text-xs text-[#B9C8D5] mb-4">
            Click below to sign in with Google and grant calendar access. You'll be redirected back here when done.
          </p>
          <Btn onClick={connect} disabled={connecting} data-testid="gcal-connect-btn">
            <Calendar className="w-4 h-4 mr-2" />
            {connecting ? "Redirecting…" : "Connect Google Calendar"}
          </Btn>
        </Card>
      )}

      {/* How it works */}
      <Card title="How It Works">
        <div className="space-y-3 text-xs text-[#B9C8D5] leading-relaxed">
          <div className="flex gap-3">
            <span className="text-[#FFD166] font-bold shrink-0">Delivery event</span>
            <span>Created on the rental <strong className="text-[#F6F1E4]">start date</strong> at the customer's delivery window. Title: <code className="bg-black/30 px-1 rounded">#EGR-XXXXX | Jane Smith (Delivery)</code></span>
          </div>
          <div className="flex gap-3">
            <span className="text-[#FFD166] font-bold shrink-0">Pickup event&nbsp;&nbsp;</span>
            <span>Created on the rental <strong className="text-[#F6F1E4]">end date</strong> at the customer's pickup window. Title: <code className="bg-black/30 px-1 rounded">#EGR-XXXXX | Jane Smith (Pickup)</code></span>
          </div>
          <div className="flex gap-3">
            <span className="text-[#FFD166] font-bold shrink-0">Description&nbsp;&nbsp;&nbsp;</span>
            <span>Both events include customer info, resort/address, delivery time window, all rented items, and order total.</span>
          </div>
          <div className="flex gap-3">
            <span className="text-[#FFD166] font-bold shrink-0">Auto-sync&nbsp;&nbsp;&nbsp;&nbsp;</span>
            <span>Events are created automatically when a booking is paid. You can also sync any reservation manually from the Reservations page.</span>
          </div>
        </div>
      </Card>
    </div>
  );
};
